// Trust Exchange (C6.1) — publish-once/consume-many security-measure evidence over the
// tenant-worker /trust/* routes. Publish a measure, see the measures visible to this tenant
// (own + public + authenticated + subscribed via the visibility RLS), and subscribe (consume). PLAN-C6.1.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@sympllex/api-client";
import { api } from "../lib/api";
import { API_BASE } from "../lib/env";
import { PageHeader } from "../components/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TRUST_TIERS = ["self_attested", "sympllex_ai_verified", "third_party_audited", "certificate_backed"] as const;
const VISIBILITIES = ["public", "authenticated_only", "subscribers_only", "engagement_only"] as const;
const SCOPE_KINDS = ["organisation_wide", "service_specific", "engagement_specific"] as const;

export function TrustExchangeRoute() {
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [scopeKind, setScopeKind] = useState<string>("organisation_wide");
  const [trustTier, setTrustTier] = useState<string>("self_attested");
  const [visibility, setVisibility] = useState<string>("public");

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["trust", "measures"],
    queryFn: () => api.listVisibleMeasures() as Promise<Array<Record<string, unknown>>>,
  });
  const rows = data ?? [];

  const publish = useMutation({
    mutationFn: () => api.publishMeasure({ measureTitle: title, scopeKind, trustTier, visibility }),
    onSuccess: () => { setTitle(""); qc.invalidateQueries({ queryKey: ["trust", "measures"] }); },
  });
  const consume = useMutation({
    mutationFn: (publishedMeasureId: string) => api.consumeMeasure({ publishedMeasureId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["trust", "consumed"] }),
  });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Trust Exchange"
        description="Publish your Art.32 security measures once; controllers subscribe and they flow into their register."
        actions={isFetching ? <span className="text-sm text-muted-foreground">updating…</span> : undefined}
      />

      <TrustPagePanel />

      <Card>
        <CardContent>
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(e) => { e.preventDefault(); if (title.trim()) publish.mutate(); }}
          >
            <div className="grid flex-1 gap-1.5 min-w-60">
              <Label htmlFor="trust-title">measure</Label>
              <Input id="trust-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="measure title (e.g. AES-256 at rest)" />
            </div>
            <LabeledSelect id="trust-scope" label="scope" value={scopeKind} onChange={setScopeKind} options={SCOPE_KINDS} />
            <LabeledSelect id="trust-tier" label="tier" value={trustTier} onChange={setTrustTier} options={TRUST_TIERS} />
            <LabeledSelect id="trust-visibility" label="visibility" value={visibility} onChange={setVisibility} options={VISIBILITIES} />
            <Button type="submit" disabled={!title.trim() || publish.isPending}>{publish.isPending ? "Publishing…" : "Publish"}</Button>
          </form>
          {publish.isError && <p className="mt-2 text-sm text-destructive">{publish.error instanceof ApiError ? `Server ${publish.error.status}: ${JSON.stringify(publish.error.body)}` : (publish.error as Error).message}</p>}
        </CardContent>
      </Card>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {error && <p className="text-sm text-destructive">Failed: {(error as Error).message}</p>}
      {!isLoading && !error && rows.length === 0 && <p className="text-sm text-muted-foreground">No measures visible yet — publish one above.</p>}

      {rows.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>measure</TableHead>
              <TableHead>tier</TableHead>
              <TableHead>visibility</TableHead>
              <TableHead>status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => {
              const id = r.id as string;
              return (
                <TableRow key={id ?? i}>
                  <TableCell className="font-medium">{String(r.measureTitle ?? "—")}</TableCell>
                  <TableCell><Badge variant="outline">{String(r.trustTier ?? "—")}</Badge></TableCell>
                  <TableCell><Badge variant="outline">{String(r.visibility ?? "—")}</Badge></TableCell>
                  <TableCell><Badge variant="secondary">{String(r.publicationStatus ?? "—")}</Badge></TableCell>
                  <TableCell><Button variant="outline" size="sm" onClick={() => consume.mutate(id)} disabled={consume.isPending}>Subscribe</Button></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      {consume.isError && <p className="text-sm text-destructive">{(consume.error as Error).message}</p>}
    </div>
  );
}

// D10 — manage the tenant's public Trust page (slug / summary / public toggle) + show the public URL.
function TrustPagePanel() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["my-trust-page"], queryFn: () => api.getMyTrustPage() as Promise<Record<string, unknown> | null> });
  const page = data ?? null;
  const [slug, setSlug] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [isPublic, setIsPublic] = useState<boolean | null>(null);
  const v = {
    slug: slug ?? String(page?.slug ?? ""),
    summary: summary ?? String(page?.displaySummaryMarkdown ?? ""),
    isPublic: isPublic ?? Boolean(page?.isPublic ?? false),
  };
  const save = useMutation({
    mutationFn: () => api.updateMyTrustPage({ slug: v.slug.trim().toLowerCase(), displaySummaryMarkdown: v.summary.trim() || null, isPublic: v.isPublic }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["my-trust-page"] }),
  });
  const publicUrl = v.slug ? `${API_BASE.tenant}/trust-page/${v.slug.trim().toLowerCase()}` : "";
  return (
    <Card>
      <CardContent>
        <details>
          <summary className="cursor-pointer text-sm font-semibold">Public trust page (D10)</summary>
          <p className="my-2 text-sm text-muted-foreground">A public, shareable assurance page listing your visibility=&ldquo;public&rdquo; security measures. The page title is your organisation name.</p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="trust-slug">slug</Label>
              <Input id="trust-slug" value={v.slug} onChange={(e) => setSlug(e.target.value)} placeholder="slug (e.g. acme-bv)" />
            </div>
            <Label htmlFor="trust-public" className="h-8">
              <Checkbox id="trust-public" checked={v.isPublic} onCheckedChange={(c) => setIsPublic(c === true)} /> Public
            </Label>
          </div>
          <div className="mt-2 grid gap-1.5">
            <Label htmlFor="trust-summary">summary</Label>
            <Textarea id="trust-summary" value={v.summary} onChange={(e) => setSummary(e.target.value)} placeholder="intro / summary (optional)" rows={3} />
          </div>
          <div className="mt-2 flex items-center gap-3">
            <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending || !v.slug.trim()}>{save.isPending ? "Saving…" : "Save page"}</Button>
            {v.isPublic && publicUrl && <a href={publicUrl} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline">Open public page →</a>}
            {save.isSuccess && <span className="text-sm text-emerald-600">Saved.</span>}
            {save.isError && <span className="text-sm text-destructive">{(save.error as Error).message}</span>}
          </div>
        </details>
      </CardContent>
    </Card>
  );
}

function LabeledSelect({ id, label, value, onChange, options }: { id: string; label: string; value: string; onChange: (v: string) => void; options: readonly string[] }) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id}><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
